import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authService } from '../services/authService';


interface UserPermissions {
  auto_approve_websites: boolean;
  can_create_analyses: boolean;
  can_manage_billing: boolean;
  can_invite_members: boolean;
}

interface User {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  role?: string;
  avatar_url?: string;
  tenant_id?: number;
  is_account_owner?: boolean;
  permissions?: UserPermissions;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  loading: boolean;
  isImpersonating: boolean;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
  impersonate: (userId: string) => Promise<boolean>;
  stopImpersonating: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// For backwards compatibility
export const useHybridAuth = useAuth;

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const [isImpersonating, setIsImpersonating] = useState(false);

  /**
   * Refresh user data from the backend
   */
  const refreshUser = useCallback(async () => {
    try {
      const response = await authService.getCurrentUser();
      if (response) {
        setUser(response.user || response);
        setIsAuthenticated(true);
        setIsImpersonating(response.is_impersonating || false);
      } else {
        setIsAuthenticated(false);
        setUser(null);
        setIsImpersonating(false);
      }
    } catch (error) {
      console.error('Failed to refresh user:', error);
      setIsAuthenticated(false);
      setUser(null);
      setIsImpersonating(false);
    }
  }, []);

  /**
   * Impersonate a user (admin only)
   */
  const impersonate = useCallback(async (userId: string): Promise<boolean> => {
    try {
      const response = await authService.impersonate(userId);
      if (response.success) {
        await refreshUser();
        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to impersonate user:', error);
      return false;
    }
  }, [refreshUser]);

  /**
   * Stop impersonating and return to admin account
   */
  const stopImpersonating = useCallback(async (): Promise<boolean> => {
    try {
      const response = await authService.stopImpersonating();
      if (response.success) {
        await refreshUser();
        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to stop impersonating:', error);
      return false;
    }
  }, [refreshUser]);

  /**
   * Sign out from Laravel session
   */
  const signOut = useCallback(async () => {
    try {
      await authService.logout();
      setIsAuthenticated(false);
      setUser(null);
    } catch (error) {
      console.error('Sign out failed:', error);
    }
  }, []);

  // Initialize authentication once when component mounts
  useEffect(() => {
    if (initialized) {
      return;
    }

    const initAuth = async () => {
      try {
        setLoading(true);

        // Check if user has an active Laravel session
        const isAuth = await authService.checkAuth();

        if (isAuth) {
          const response = await authService.getCurrentUser();
          if (response) {
            setUser(response.user || response);
            setIsAuthenticated(true);
            setIsImpersonating(response.is_impersonating || false);
          }
        }
      } catch (error) {
        console.error('Auth initialization failed:', error);
      } finally {
        setLoading(false);
        setInitialized(true);
      }
    };

    initAuth();
  }, [initialized]);


  const value: AuthContextType = {
    isAuthenticated,
    user,
    loading,
    isImpersonating,
    signOut,
    refreshUser,
    impersonate,
    stopImpersonating,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// For backwards compatibility
export const HybridAuthProvider = AuthProvider;